import React, { useRef, useState, useEffect } from "react";
import { motion, useTransform, useScroll } from "framer-motion";
import { api } from "../../lib/api";

// Individual Project Item with Scroll Animation for Mobile
const MobileProjectItem = ({ work, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 90%", "start 50%"]
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [50, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.95, 1]);

  const handleClick = (e) => {
    if (work.redirect_enabled && work.redirect_url) {
      e.stopPropagation();
      let url = work.redirect_url;
      // Add https:// if no protocol specified
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      ref={ref}
      style={{ opacity, y, scale }}
      className={`group relative w-full bg-black ${work.redirect_enabled && work.redirect_url ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      <div className="aspect-[4/3] w-full overflow-hidden mb-6">
        <img
          src={work.image_url}
          alt={work.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div>
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-accent font-mono text-[10px] md:text-xs uppercase tracking-widest border border-accent/20 px-2 py-1">{work.category}</span>
          {work.tags.slice(0, 2).map(tag => (
            <span key={tag} className="text-gray-500 font-mono text-[10px] md:text-xs uppercase tracking-widest border border-white/10 px-2 py-1">{tag}</span>
          ))}
        </div>
        <h3 className="text-3xl md:text-5xl font-display font-bold text-white">{work.title}</h3>
      </div>
    </motion.div>
  );
};

// Sub-component for Mobile View
const MobileProjects = ({ projects }) => {
  return (
    <section id="work" className="relative bg-neutral-900 py-20 px-4 md:px-12">
      <div className="mb-12 max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
          SELECTED <br /> <span className="text-accent">WORKS</span>
        </h2>
        <p className="text-gray-400 text-base md:text-lg">
          Our most ambitious projects.
        </p>
      </div>
      <div className="flex flex-col gap-12 md:gap-16 max-w-7xl mx-auto">
        {projects.map((work, i) => (
          <MobileProjectItem key={i} work={work} index={i} />
        ))}
      </div>
    </section>
  );
};

// Sub-component for Desktop View
const DesktopProjects = ({ projects }) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-75%"]);

  return (
    <section ref={targetRef} id="work" className="relative h-[400vh] bg-neutral-900 hidden lg:block">
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <motion.div style={{ x }} className="flex gap-40 pl-20">
          <div className="flex flex-col justify-center min-w-[40vw]">
            <h2 className="text-8xl font-display font-bold text-white mb-8">
              SELECTED <br /> <span className="text-accent">WORKS</span>
            </h2>
            <p className="text-gray-400 text-xl max-w-md">
              A curated selection of our most ambitious projects, pushing the boundaries of web technology.
            </p>
          </div>
          {projects.map((work, i) => (
            <div
              key={i}
              className={`group relative h-[70vh] w-[40vw] flex-shrink-0 overflow-hidden bg-black hover-trigger ${work.redirect_enabled && work.redirect_url ? 'cursor-pointer' : 'cursor-none'}`}
              onClick={() => {
                if (work.redirect_enabled && work.redirect_url) {
                  let url = work.redirect_url;
                  if (!url.startsWith('http://') && !url.startsWith('https://')) {
                    url = 'https://' + url;
                  }
                  window.open(url, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              <div className="absolute inset-0 bg-accent/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 mix-blend-overlay" />
              <img
                src={work.image_url}
                alt={work.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
              />
              <div className="absolute bottom-0 left-0 p-8 z-20 w-full bg-gradient-to-t from-black/90 to-transparent">
                <p className="text-accent font-mono text-sm uppercase tracking-widest mb-2">{work.category}</p>
                <h3 className="text-4xl md:text-5xl font-display font-bold text-white">{work.title}</h3>
                <div className="flex gap-2 mt-4">
                  {work.tags.map(tag => (
                    <span key={tag} className="text-xs font-mono border border-white/20 px-2 py-1 text-gray-400">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Projects = () => {
  const [isMobile, setIsMobile] = useState(true);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get("/projects");
        setProjects(res.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();

    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (loading) {
    return (
      <section className="bg-neutral-900 h-[50vh] flex items-center justify-center">
        <div className="text-accent animate-pulse font-mono">Loading Projects...</div>
      </section>
    )
  }

  return isMobile ? (
    <MobileProjects projects={projects} />
  ) : (
    <DesktopProjects projects={projects} />
  );
};

export default Projects;
